/**
 * Centralized audio SFX via Web Audio API.
 * Each interaction type has a distinct, short sound.
 */

let ctx: AudioContext | null = null;

const getCtx = (): AudioContext => {
  if (!ctx || ctx.state === "closed") ctx = new AudioContext();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

/** Quick utility: play an oscillator note */
const note = (
  audioCtx: AudioContext,
  freq: number,
  type: OscillatorType,
  start: number,
  dur: number,
  vol: number,
  dest: AudioNode
) => {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(vol, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
  osc.connect(gain).connect(dest);
  osc.start(start);
  osc.stop(start + dur);
};

/** Subtle click — for generic button taps */
export const sfxTap = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.08;
    master.connect(c.destination);
    note(c, 1800, "sine", t, 0.04, 0.6, master);
    note(c, 2400, "sine", t + 0.015, 0.03, 0.3, master);
  } catch {}
};

/** Soft thud — for links / navigation */
export const sfxLinkTap = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.06;
    master.connect(c.destination);
    note(c, 1200, "sine", t, 0.05, 0.4, master);
    note(c, 800, "sine", t + 0.02, 0.04, 0.2, master);
  } catch {}
};

/** Tab switch — crisp tick */
export const sfxTabTap = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.07;
    master.connect(c.destination);
    note(c, 2200, "triangle", t, 0.03, 0.5, master);
    note(c, 3000, "sine", t + 0.01, 0.025, 0.25, master);
  } catch {}
};

/** Menu item — muted pop */
export const sfxMenuTap = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.05;
    master.connect(c.destination);
    note(c, 1400, "sine", t, 0.035, 0.35, master);
    note(c, 1900, "triangle", t + 0.015, 0.03, 0.15, master);
  } catch {}
};

/** Input focus — gentle ding */
export const sfxInputTap = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.04;
    master.connect(c.destination);
    note(c, 2600, "sine", t, 0.05, 0.3, master);
  } catch {}
};

/** Upload whoosh — rising sweep */
export const sfxUpload = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.1;
    master.connect(c.destination);

    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(300, t);
    osc.frequency.exponentialRampToValueAtTime(1200, t + 0.35);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.5, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + 0.45);

    // shimmer overtone
    note(c, 880, "triangle", t + 0.1, 0.25, 0.15, master);
  } catch {}
};

/** Success — cheerful ascending chime (three notes) */
export const sfxSuccess = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.12;
    master.connect(c.destination);

    note(c, 523.25, "sine", t, 0.15, 0.5, master);       // C5
    note(c, 659.25, "sine", t + 0.1, 0.15, 0.5, master);  // E5
    note(c, 783.99, "sine", t + 0.2, 0.25, 0.6, master);  // G5
    // sparkle
    note(c, 1567.98, "sine", t + 0.25, 0.2, 0.15, master); // G6
  } catch {}
};

/** Reward — coin/points ding with shimmering tail */
export const sfxReward = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.13;
    master.connect(c.destination);

    // Coin hit
    note(c, 1318.5, "sine", t, 0.08, 0.6, master);       // E6
    note(c, 2637, "sine", t, 0.06, 0.2, master);          // E7 harmonic
    // Shimmer tail
    note(c, 1975.53, "sine", t + 0.06, 0.3, 0.3, master); // B6
    note(c, 2349.32, "triangle", t + 0.12, 0.35, 0.12, master); // D7
    // Sub warmth
    note(c, 659.25, "sine", t + 0.02, 0.2, 0.15, master); // E5
  } catch {}
};

/** Like / favorite — bubbly pop */
export const sfxLike = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.1;
    master.connect(c.destination);

    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(1400, t + 0.06);
    osc.frequency.exponentialRampToValueAtTime(900, t + 0.15);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.6, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + 0.2);

    // pop overtone
    note(c, 1800, "sine", t + 0.02, 0.08, 0.2, master);
  } catch {}
};

/** Unlike — short deflate */
export const sfxUnlike = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.06;
    master.connect(c.destination);

    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.1);
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + 0.15);
  } catch {}
};

/** Error — low buzz */
export const sfxError = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.08;
    master.connect(c.destination);

    note(c, 180, "sawtooth", t, 0.15, 0.3, master);
    note(c, 170, "sawtooth", t + 0.02, 0.13, 0.25, master); // slight detune
  } catch {}
};

/** Comment sent — soft plink */
export const sfxComment = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.09;
    master.connect(c.destination);

    note(c, 1046.5, "sine", t, 0.1, 0.4, master);  // C6
    note(c, 1318.5, "triangle", t + 0.05, 0.12, 0.2, master); // E6
  } catch {}
};

/** Navigation / tab switch — subtle whoosh */
export const sfxNavigate = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.05;
    master.connect(c.destination);

    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(800, t);
    osc.frequency.exponentialRampToValueAtTime(1600, t + 0.08);
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(gain).connect(master);
    osc.start(t);
    osc.stop(t + 0.12);
  } catch {}
};

/** Republic Points page — treasure chest / coin cascade */
export const sfxPointsPage = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.11;
    master.connect(c.destination);

    // Opening sparkle sweep
    const sweep = c.createOscillator();
    const sweepGain = c.createGain();
    sweep.type = "sine";
    sweep.frequency.setValueAtTime(400, t);
    sweep.frequency.exponentialRampToValueAtTime(2000, t + 0.25);
    sweepGain.gain.setValueAtTime(0, t);
    sweepGain.gain.linearRampToValueAtTime(0.3, t + 0.03);
    sweepGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    sweep.connect(sweepGain).connect(master);
    sweep.start(t);
    sweep.stop(t + 0.35);

    // Coin cascade — descending chimes
    note(c, 1318.5, "sine", t + 0.08, 0.12, 0.5, master);  // E6
    note(c, 1567.98, "sine", t + 0.15, 0.12, 0.45, master); // G6
    note(c, 2093, "sine", t + 0.22, 0.15, 0.5, master);     // C7
    note(c, 2637, "sine", t + 0.30, 0.2, 0.35, master);     // E7

    // Warm sub bass
    note(c, 261.63, "sine", t + 0.05, 0.35, 0.2, master);   // C4

    // Shimmer tail
    note(c, 3951.07, "sine", t + 0.35, 0.3, 0.08, master);  // B7
    note(c, 2793.83, "triangle", t + 0.38, 0.25, 0.06, master); // F7
  } catch {}
};

/** Rank-up fanfare — triumphant ascending brass-like sequence */
export const sfxRankUp = () => {
  try {
    const c = getCtx();
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.14;
    master.connect(c.destination);

    // Deep impact hit
    note(c, 130.81, "sawtooth", t, 0.5, 0.35, master);       // C3
    note(c, 130.81, "sine", t, 0.6, 0.25, master);            // C3 sub

    // Ascending fanfare (brass-like sawtooth)
    note(c, 523.25, "sawtooth", t + 0.1, 0.2, 0.3, master);  // C5
    note(c, 659.25, "sawtooth", t + 0.25, 0.2, 0.3, master); // E5
    note(c, 783.99, "sawtooth", t + 0.4, 0.2, 0.35, master); // G5
    note(c, 1046.5, "sawtooth", t + 0.55, 0.4, 0.4, master); // C6

    // Bright harmonics
    note(c, 1318.5, "sine", t + 0.6, 0.3, 0.25, master);     // E6
    note(c, 1567.98, "sine", t + 0.65, 0.35, 0.2, master);   // G6
    note(c, 2093, "sine", t + 0.7, 0.5, 0.15, master);       // C7

    // Shimmer tail
    note(c, 3951.07, "sine", t + 0.8, 0.6, 0.06, master);    // B7
    note(c, 4186.01, "sine", t + 0.85, 0.5, 0.05, master);   // C8
    note(c, 2637.02, "triangle", t + 0.9, 0.4, 0.04, master); // E7

    // Warm resolution chord
    note(c, 261.63, "sine", t + 0.55, 0.8, 0.15, master);    // C4
    note(c, 329.63, "sine", t + 0.55, 0.8, 0.12, master);    // E4
    note(c, 392, "sine", t + 0.55, 0.8, 0.1, master);        // G4
  } catch {}
};
